import { showEvent$ } from '@psl/functions/firebase/observables/showEvent$';
import { userProfileDoc } from '@psl/functions/firebase/refs/profileDocRef';
import { storeFrontDoc } from '@psl/functions/firebase/refs/storeFrontDocRef';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  combineLatest,
  defer,
  Observable,
  of,
  OperatorFunction,
  throwError,
  timer,
} from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  currentUserId,
  useCurrentUserId,
} from '../../../../functions/auth/utils';
import { EntityRef } from '../../../../functions/EntityRef';
import { firestore } from '../../../../functions/firebase/firebaseAccess';
import {
  collectionDataWithId,
  docDataWithId,
  memorize,
  WithID,
} from '../../../../functions/firebase/rxfireExtensions';
import { useObservable } from '../../../../functions/hooks/useObservable';
import { useLocalObservable } from '../../../../functions/rxReact/useLocalObservable';
import {
  CurrentShowSpecialUrlCode,
  DecodedSpecialUrlCode,
  SpecialUrlCode,
} from '../../../../functions/urlCodeHelpers';
import { useDeviceId } from '../../../../functions/user/sessionTracker';
import * as ClientModels from '../../../../models/ClientModels';
import {
  LiveStream,
  Profile,
  PromotionPage,
  ShowEvent,
  ShowPermission,
  StoreFront,
  UpcomingShow,
} from '../../../../models/ClientModels';
import { shareReplay, switchMapIf } from '../../../../utils/rxExtensions';
import { assertNever } from '../../../../utils/typeHelper';
import {
  eventShowCodeQuery,
  eventShowQuery,
  FIVE_MINUTES,
  liveStreamDoc,
  profileCodeQuery,
  profileQuery,
  promotionDoc,
  sellerLatestLiveStreamQuery,
  sellerNextUpcomingShow,
  storeDoc,
  UPCOMING_SHOW_OVER_TIME_LIMIT,
  upcomingShowDoc,
} from './useCurrentShow.firebase';
import { showInfo } from './useCurrentShow.utils';

export type CurrentShowRef =
  | EntityRef.UpcomingShow
  | EntityRef.LiveShow
  | EntityRef.Event
  | EntityRef.Promotion
  | EntityRef.Seller
  | EntityRef.Replay;

export type CurrentShow =
  | CurrentShow.UpcomingShow
  | CurrentShow.LiveShow
  | CurrentShow.Event
  | CurrentShow.Promotion
  | CurrentShow.Seller
  | CurrentShow.Replay;
export namespace CurrentShow {
  export interface UpcomingShow {
    type: 'upcomingShow';
    upcomingShow: WithID<ClientModels.UpcomingShow>;
  }
  export namespace UpcomingShow {
    export function create(
      upcomingShow: WithID<ClientModels.UpcomingShow>,
    ): UpcomingShow {
      return {
        type: 'upcomingShow',
        upcomingShow,
      };
    }
  }

  export interface LiveShow {
    type: 'liveShow';
    liveStream: WithID<LiveStream>;
  }
  export namespace LiveShow {
    export function create(liveStream: WithID<LiveStream>): LiveShow {
      return {
        type: 'liveShow',
        liveStream,
      };
    }
  }

  export interface Replay {
    type: 'replay';
    liveStream: WithID<LiveStream>;
  }

  export namespace Replay {
    export function create(liveStream: WithID<LiveStream>): Replay {
      return {
        type: 'replay',
        liveStream,
      };
    }
  }

  export interface Event {
    type: 'event';
    event: WithID<ShowEvent>;
    asCover?: boolean;
  }
  export namespace Event {
    export function create(
      event: WithID<ShowEvent>,
      extra: { asCover?: boolean } = {},
    ): Event {
      return {
        type: 'event',
        event,
        asCover: extra.asCover,
      };
    }
  }

  export interface Promotion {
    type: 'promotion';
    promotion: WithID<PromotionPage>;
  }
  export namespace Promotion {
    export function create(promotion: WithID<PromotionPage>): Promotion {
      return {
        type: 'promotion',
        promotion,
      };
    }
  }

  type SellerSceneType = 'seller-profile' | 'inventory';

  export interface Seller {
    type: 'seller';
    seller: WithID<Profile>;
    store: WithID<StoreFront>;
    scene?: SellerSceneType;
    // indicates from where the seller's profile page originates
    sourceShow?: CurrentShow;
    reelOnly?: boolean;
  }

  export namespace Seller {
    export function create(
      seller: WithID<Profile>,
      store: WithID<StoreFront>,
      options?: {
        sourceShow?: CurrentShow;
        scene?: SellerSceneType;
        reelOnly?: boolean;
      },
    ): Seller {
      return {
        type: 'seller',
        seller,
        store,
        scene: options?.scene,
        sourceShow: options?.sourceShow,
        reelOnly: options?.reelOnly,
      };
    }
  }

  export type HasSeller = Seller | LiveShow | UpcomingShow | Replay;
  export const isHasSeller = (show: CurrentShow): show is HasSeller => {
    return (
      show.type === 'seller' ||
      show.type === 'liveShow' ||
      show.type === 'upcomingShow' ||
      show.type === 'replay'
    );
  };
  export function getSeller(show: CurrentShow.HasSeller): WithID<Profile>;
  export function getSeller(show: CurrentShow): null | WithID<Profile>;
  export function getSeller(show: CurrentShow): null | WithID<Profile> {
    if (isHasSeller(show)) {
      if (show.type === 'seller') {
        return show.seller;
      } else if (show.type === 'liveShow') {
        return {
          id: show.liveStream.primary.userId,
          ...show.liveStream.primary.profile,
        };
      } else if (show.type === 'upcomingShow') {
        return {
          id: show.upcomingShow.userId,
          ...show.upcomingShow.user,
        };
      } else if (show.type === 'replay') {
        return {
          id: show.liveStream.primary.userId,
          ...show.liveStream.primary.profile,
        };
      } else {
        assertNever(show);
      }
    }
    return null;
  }

  export const fromSeller$ = (
    seller: WithID<Profile>,
  ): Observable<CurrentShow.HasSeller> => {
    return combineLatest([
      sellerLiveStreamObservable(seller.id),
      sellerNextUpcomingShowNotOverTime(seller.id),
    ]).pipe(
      switchMap(([liveStream, upcomingShow]) => {
        if (liveStream != null) {
          return of(CurrentShow.LiveShow.create(liveStream));
        }
        if (upcomingShow != null) {
          return of(CurrentShow.UpcomingShow.create(upcomingShow));
        }
        return storeQuery$(seller.id).pipe(
          map(store => CurrentShow.Seller.create(seller, store)),
        );
      }),
    );
  };

  export const fromSellerWithReelOnly$ = (
    seller: WithID<Profile>,
  ): Observable<CurrentShow.Seller> => {
    return storeQuery$(seller.id).pipe(
      map(store =>
        CurrentShow.Seller.create(seller, store, { reelOnly: true }),
      ),
    );
  };

  const fromSpecialUrlCode$ = (
    code: CurrentShowSpecialUrlCode,
    options?: { reelOnly?: boolean },
  ): Observable<CurrentShow> => {
    const decoded = DecodedSpecialUrlCode.decodeCurrentShowSpecialUrlCode(code);

    switch (decoded.type) {
      case 'liveShow':
        return liveShow$(decoded.liveShowId).pipe(
          map(CurrentShow.LiveShow.create),
        );
      case 'upcomingShow':
        return upcomingShow$(decoded.upcomingShowId).pipe(
          map(CurrentShow.UpcomingShow.create),
        );
      case 'replay':
        return liveShow$(decoded.liveShowId).pipe(
          map(CurrentShow.Replay.create),
        );
      case 'event':
        return event$(decoded.eventId).pipe(
          map(event => CurrentShow.Event.create(event)),
        );
      case 'promotion':
        return promotion$(decoded.promotionId).pipe(
          map(CurrentShow.Promotion.create),
        );
      case 'profile':
        return seller$(decoded.profileId).pipe(
          switchMap(options?.reelOnly ? fromSellerWithReelOnly$ : fromSeller$),
        );
      default:
        assertNever(decoded);
    }
  };

  export const fromLiveShow$ = (
    id: string,
  ): Observable<CurrentShow.LiveShow> => {
    return liveShow$(id).pipe(map(CurrentShow.LiveShow.create));
  };

  export const fromReplay$ = (id: string): Observable<CurrentShow.Replay> => {
    return liveShow$(id).pipe(map(CurrentShow.Replay.create));
  };

  export const fromUpcomingShow$ = (
    id: string,
  ): Observable<CurrentShow.UpcomingShow> => {
    return upcomingShow$(id).pipe(map(CurrentShow.UpcomingShow.create));
  };

  export const fromEvent$ = (id: string): Observable<CurrentShow.Event> => {
    return event$(id).pipe(map(event => CurrentShow.Event.create(event)));
  };

  const sellerOrEventFromUrlCode$ = (
    code: string,
    options?: { reelOnly?: boolean },
  ): Observable<CurrentShow.Event | CurrentShow.HasSeller> =>
    combineLatest([profileFromUrlCode$(code), eventFromUrlCode$(code)]).pipe(
      switchMap(([seller, event]) => {
        if (event != null) {
          return of(CurrentShow.Event.create(event));
        }

        if (seller == null) {
          const error = new Error(`${code} doesn't not exist on our record`);
          error.name = 'CODE_NOT_EXIST';
          return throwError(() => error);
        }

        return options?.reelOnly
          ? fromSellerWithReelOnly$(seller)
          : fromSeller$(seller);
      }),
    );

  export const fromUrlCode$ = memorize(
    (code: string): Observable<CurrentShow> => {
      const options: { reelOnly?: boolean } = {};
      if (SpecialUrlCode.isUrlCodeWithReelOnly(code)) {
        options.reelOnly = true;
        code = SpecialUrlCode.urlCodeWithReelOnlyRemoved(code);
      }
      return (
        SpecialUrlCode.isCurrentShowSpecialUrlCode(code)
          ? fromSpecialUrlCode$(code, options)
          : sellerOrEventFromUrlCode$(code, options)
      ).pipe(upcomingShowToLiveStream());
    },
  );

  export function toShowRef(show: CurrentShow): CurrentShowRef {
    switch (show.type) {
      case 'liveShow':
        return { type: 'liveShow', liveStreamId: show.liveStream.id };
      case 'upcomingShow':
        return { type: 'upcomingShow', upcomingShowId: show.upcomingShow.id };
      case 'replay':
        return { type: 'replay', liveStreamId: show.liveStream.id };
      case 'event':
        return { type: 'event', eventId: show.event.id };
      case 'seller':
        return { type: 'seller', sellerId: show.seller.id };
      case 'promotion':
        return { type: 'promotion', promotionId: show.promotion.id };
    }
    assertNever(show);
  }

  export function fromShowRef$(
    showRef: CurrentShowRef,
  ): Observable<CurrentShow> {
    let show$: Observable<CurrentShow>;
    switch (showRef.type) {
      case 'event':
        show$ = event$(showRef.eventId).pipe(
          map(event => CurrentShow.Event.create(event, { asCover: false })),
        );
        break;
      case 'liveShow':
        show$ = liveShow$(showRef.liveStreamId).pipe(
          map(CurrentShow.LiveShow.create),
        );
        break;
      case 'replay':
        show$ = liveShow$(showRef.liveStreamId).pipe(
          map(CurrentShow.Replay.create),
        );
        break;
      case 'promotion':
        show$ = promotion$(showRef.promotionId).pipe(
          map(CurrentShow.Promotion.create),
        );
        break;
      case 'seller':
        show$ = combineLatest([
          seller$(showRef.sellerId),
          store$(showRef.sellerId),
        ]).pipe(
          map(([seller, store]) => CurrentShow.Seller.create(seller, store)),
        );
        break;
      case 'upcomingShow':
        show$ = upcomingShow$(showRef.upcomingShowId).pipe(
          map(CurrentShow.UpcomingShow.create),
        );
        break;
      default:
        assertNever(showRef);
    }

    return show$.pipe(upcomingShowToLiveStream());
  }

  export function fromShow$(show: CurrentShow): Observable<CurrentShow> {
    return fromShowRef$(toShowRef(show));
  }

  function upcomingShowToLiveStream(): OperatorFunction<
    CurrentShow,
    CurrentShow
  > {
    return switchMapIf(
      (show): show is CurrentShow.UpcomingShow =>
        show.type === 'upcomingShow' && !!show.upcomingShow?.liveStreamId,
      show =>
        liveShow$(show.upcomingShow.liveStreamId!).pipe(
          map(CurrentShow.LiveShow.create),
        ),
    );
  }

  function event$(eventId: string): Observable<WithID<ShowEvent>> {
    return showEvent$(eventId);
  }
  function liveShow$(showId: string): Observable<WithID<LiveStream>> {
    return docDataWithId(liveStreamDoc(showId));
  }
  function promotion$(promotionId: string): Observable<WithID<PromotionPage>> {
    return docDataWithId(promotionDoc(promotionId));
  }
  function seller$(sellerId: string): Observable<WithID<Profile>> {
    return docDataWithId(userProfileDoc(sellerId));
  }
  function store$(storeId: string): Observable<WithID<StoreFront>> {
    return docDataWithId(storeDoc(storeId));
  }
  function upcomingShow$(
    showId: string,
  ): Observable<WithID<ClientModels.UpcomingShow>> {
    return docDataWithId(upcomingShowDoc(showId));
  }
}

const sellerNextUpcomingShowDoc = (sellerId: string) =>
  collectionDataWithId(sellerNextUpcomingShow(sellerId)).pipe(
    map(l => {
      const next = l[0];
      if (!next || next.permission === ShowPermission.None) {
        return undefined;
      } else {
        return next;
      }
    }),
  );

export function sellerNextUpcomingShowNotOverTime(
  sellerId: string,
  checkInterval = FIVE_MINUTES,
) {
  return combineLatest([
    sellerNextUpcomingShowDoc(sellerId),
    timer(0, checkInterval), // every 5 minutes to check if it is overtime.
  ]).pipe(
    map(([upcomingShow, _]: [WithID<UpcomingShow> | undefined, number]) => {
      if (
        upcomingShow != null &&
        upcomingShow.startsAt.toMillis() >=
          Date.now() - UPCOMING_SHOW_OVER_TIME_LIMIT
      ) {
        return upcomingShow;
      }
      return null;
    }),
    distinctUntilChanged(),
  );
}

export const profileFromUrlCode$ = memorize((code: string) =>
  defer(() => {
    if (SpecialUrlCode.isProfile(code)) {
      return docDataWithId(
        profileQuery(DecodedSpecialUrlCode.decodeProfile(code).profileId),
      );
    }

    return collectionDataWithId<Profile>(profileCodeQuery(code)).pipe(
      map(array => array[0] as WithID<Profile> | undefined),
    );
  }).pipe(shareReplay(1)),
);

export const eventFromUrlCode$ = memorize((code: string) =>
  defer(() => {
    if (SpecialUrlCode.isEvent(code)) {
      return docDataWithId(
        eventShowQuery(DecodedSpecialUrlCode.decodeEvent(code).eventId),
      );
    }

    return collectionDataWithId(eventShowCodeQuery(code)).pipe(
      map(a => a[0] as WithID<ShowEvent> | undefined),
    );
  }).pipe(shareReplay(1)),
);

export function sellerLiveStreamObservable(sellerId: string) {
  return collectionDataWithId<LiveStream>(
    sellerLatestLiveStreamQuery(sellerId),
  ).pipe(
    map(l => {
      const live = l[0];
      if (
        !live ||
        !live.primary.status.initialized ||
        live.permission === ShowPermission.None
      ) {
        return undefined;
      } else {
        return live;
      }
    }),
  );
}

export const useCurrentShowFromUrlCode = (urlCode: string): CurrentShow => {
  return useObservable(
    CurrentShow.fromUrlCode$(urlCode),
    `query-code-current-show-${urlCode}`,
  );
};

export const storeQuery$ = memorize((sellerId: string) =>
  docDataWithId(storeFrontDoc(sellerId)),
);

export function useStore(
  sellerId: null | undefined | string,
  defaultValue?: WithID<StoreFront> | null,
): null | WithID<StoreFront> {
  return useObservable(
    () => (sellerId ? storeQuery$(sellerId) : of(null)),
    `app-store: ${sellerId}`,
    defaultValue,
  );
}

const filterNonePermission = (showPermission: ShowPermission | undefined) => {
  return showPermission !== ShowPermission.None;
};

export const updateStoreFilterNonePermissionStream = (
  store: WithID<StoreFront>,
) => {
  let { upcomingShowId, upcomingShow, liveStreamId, liveStream } = store;
  if (!filterNonePermission(upcomingShow?.permission)) {
    upcomingShowId = undefined;
    upcomingShow = undefined;
  }
  if (!filterNonePermission(liveStream?.permission)) {
    liveStreamId = undefined;
    liveStream = undefined;
  }
  return {
    ...store,
    upcomingShowId,
    upcomingShow,
    liveStreamId,
    liveStream,
  };
};

export const storeFilterNonePermissionStreamQuery = (sellerId: string) =>
  docDataWithId(firestore().collection('stores').doc(sellerId)).pipe(
    map(updateStoreFilterNonePermissionStream),
  );

export const useStoreFilterNonePermissionStream = (
  sellerId: string,
  defaultValue?: WithID<StoreFront> | null,
) => {
  return useObservable(
    storeFilterNonePermissionStreamQuery(sellerId),
    `app-store-filter-none-stream-permission: ${sellerId}`,
    defaultValue,
  );
};

export const CurrentShowContext = createContext<{
  isNested: boolean;
  show: CurrentShow;
  liveEnded: boolean;
}>(null as any);

export const CurrentShowProvider = ({
  show,
  isNested = false,
  children,
}: {
  show: CurrentShow | null;
  isNested?: boolean;
  children: ReactNode;
}) => {
  const liveEnded = Boolean(
    show?.type === 'liveShow' &&
      (show.liveStream.primary.status.finished ||
        show.liveStream.primary.status.aborted),
  );
  const context = useMemo(
    () => ({
      show: show!,
      isNested,
      liveEnded,
    }),
    [isNested, liveEnded, show],
  );
  return (
    <CurrentShowContext.Provider value={context}>
      {children}
    </CurrentShowContext.Provider>
  );
};

export const useCurrentShow = (): CurrentShow => {
  return useContext(CurrentShowContext).show;
};

export const useCurrentShowRef = (): CurrentShowRef => {
  const show = useCurrentShow();
  return useMemo(() => CurrentShow.toShowRef(show), [show]);
};

export const useSafeCurrentShow = (): undefined | CurrentShow => {
  return useContext(CurrentShowContext)?.show;
};

export const useIsCurrentShowNested = () => {
  return useContext(CurrentShowContext).isNested;
};

export const useIsHost = () => {
  const show = useCurrentShow();
  return (
    show.type === 'liveShow' &&
    show.liveStream.primary.userId === currentUserId()
  );
};

export const useCheckIsBroadcasting = (): ((show: CurrentShow) => boolean) => {
  const uid = useCurrentUserId();
  const deviceId = useDeviceId();

  return useCallback(
    show => {
      if (show.type !== 'liveShow') return false;

      const { liveStream } = show;
      const isPrimaryStreamer =
        uid === liveStream.primary.userId &&
        liveStream.primary.device === deviceId;
      const isSecondaryStreamer = liveStream.secondary?.device === deviceId;
      return isPrimaryStreamer || isSecondaryStreamer;
    },
    [uid, deviceId],
  );
};

export const useCurrentShowAsLiveStreamDangerously = () => {
  const show = useCurrentShow();
  if (show.type !== 'liveShow') {
    throw new Error("Trying to access show as livestream when it's not");
  }
  return show.liveStream;
};

export const useLiveStreamIdDangerously = () => {
  return useCurrentShowAsLiveStreamDangerously().id;
};

export const useCurrentShowAsUpcomingOrLiveStreamDangerously = () => {
  const show = useCurrentShow();
  if (show.type !== 'upcomingShow' && show.type !== 'liveShow') {
    throw new Error(
      "Trying to access show as livestream/upcomingstream when it's not",
    );
  }
  if (show.type === 'upcomingShow') {
    return {
      type: show.type,
      show: show.upcomingShow,
    };
  }
  return {
    type: show.type,
    show: show.liveStream,
  };
};

export const useLiveOrUpComingStreamIdDangerously = () => {
  return useCurrentShowAsUpcomingOrLiveStreamDangerously().show.id;
};

export const useSubscribeShow = (show: CurrentShow): CurrentShow => {
  return useLocalObservable(() => CurrentShow.fromShow$(show), [show], show);
};

export const currentShowEquals = (a: CurrentShow, b: CurrentShow) =>
  showInfo(a).identifier === showInfo(b).identifier;
